import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ownerSelector, RootState } from "../../store/store";
import { toast } from "react-toastify";
import { Action, ThunkDispatch } from "@reduxjs/toolkit";
import { addTagToProspect, removeTagFromProspect } from "../../store/features/ownerActions";
import { fetchProspects, resolveDashboardScope } from "../../utils";
import { setProspects } from "../../store/slices/prospectsSlice";
import TagSwatch from "../tags/TagSwatch";

type TagsProps = {
  prospectId: string;
  listId: string;
  onClose: () => void;
  assignedTagIds: string[];
  prospectsPage?: number;
  prospectsPageSize?: number;
};

const Tags = ({
  prospectId,
  listId,
  onClose,
  assignedTagIds,
  prospectsPage = 1,
  prospectsPageSize = 10,
}: TagsProps) => {
  const { tags, owner, currentWorkspace, currentUser } = useSelector(ownerSelector);
  const normalizeIdString = (value: unknown): string => {
    if (value == null) return "";
    if (typeof value === "string") return value.trim();
    if (typeof value === "object" && value !== null && "_id" in value) {
      return String((value as { _id?: unknown })._id || "").trim();
    }
    return String(value).trim();
  };
  const workspace =
    typeof currentWorkspace === "string"
      ? (() => {
          try {
            return JSON.parse(currentWorkspace || "null");
          } catch {
            return null;
          }
        })()
      : currentWorkspace;
  const workspaceHint =
    normalizeIdString(workspace?._id) ||
    normalizeIdString(
      (owner as { workspaces?: Array<{ _id?: unknown }> })?.workspaces?.[0]?._id
    );
  const userHint =
    normalizeIdString(currentUser?.userId as unknown) ||
    normalizeIdString((owner as { userId?: unknown })?.userId);
  const { resolvedUserId, resolvedWorkspaceId } = resolveDashboardScope(
    userHint || undefined,
    workspaceHint || undefined
  );
  const workspaceId = String(resolvedWorkspaceId || "").trim();
  const userId = String(resolvedUserId || "").trim();
  const ownerId = normalizeIdString(owner?._id as unknown);
  const dispatch: ThunkDispatch<RootState, undefined, Action> = useDispatch();
  const [busyId, setBusyId] = useState<string | null>(null);

  const refreshProspects = async () => {
    const list = await fetchProspects(
      ownerId,
      listId,
      prospectsPage,
      prospectsPageSize,
      userId,
      workspaceId
    );
    dispatch(setProspects(list.prospects));
  };

  const toggleTag = async (tagId: string, isAssigned: boolean) => {
    setBusyId(tagId);
    try {
      const prospectIds = [prospectId];
      const res = isAssigned
        ? await dispatch(
            removeTagFromProspect({
              prospectIds,
              tagId,
              listId,
              ownerId: ownerId || undefined,
              userId: userId || undefined,
              workspaceId: workspaceId || undefined,
            })
          )
        : await dispatch(
            addTagToProspect({
              prospectIds,
              tagId,
              listId,
              ownerId: ownerId || undefined,
              userId: userId || undefined,
              workspaceId: workspaceId || undefined,
            })
          );

      if (res.meta.requestStatus === "fulfilled" && (res.payload as { success?: boolean })?.success) {
        await refreshProspects();
      } else if (res.meta.requestStatus === "fulfilled") {
        toast.error((res.payload as { message?: string }).message || "Could not update tag");
      } else {
        toast.error("Could not update tag");
      }
    } catch {
      toast.error("Could not update tag");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="w-[220px] rounded-[8px] border border-light-grey bg-white py-1 shadow-lg">
      <div className="max-h-[240px] overflow-y-auto slimScrollbar">
        {tags.length === 0 ? (
          <p className="px-3 py-3 text-xs text-dark-grey">Create tags from the header menu first.</p>
        ) : (
          <ul>
            {tags.map((tag) => {
              const checked = assignedTagIds.includes(tag._id);
              const disabled = busyId === tag._id;
              return (
                <li key={tag._id}>
                  <label
                    className={`flex cursor-pointer items-center gap-2 px-3 py-2 text-xs text-dark-black hover:bg-primary ${
                      disabled ? "pointer-events-none opacity-60" : ""
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={disabled}
                      onChange={() => toggleTag(tag._id, checked)}
                      className="h-3.5 w-3.5 shrink-0 rounded-[3px] border-light-grey text-brand-blue focus:ring-brand-blue"
                    />
                    <TagSwatch color={tag.color} size="sm" />
                    <span className="min-w-0 flex-1 truncate">{tag.name}</span>
                  </label>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default Tags;
